import React, { AriaRole } from 'react';
import css from '@styled-system/css';
import styled from 'styled-components';
import { compose, space, variant } from 'styled-system';

import {
  fontWeight as fontWeightSystem,
  textAlign,
  textColor,
  textDecoration,
  textTransform,
} from '@rover/kibble/styles';

import roverTheme from '../../themes/roverTheme';
import Box from '../Box';
import { PolymorphicBoxPropsHelper } from '../Box/Box.common';

import { TextCommonProps } from './Text.common';

/** Valid HTML text element types */
export type TextNodes = 'span' | 'div' | 'label' | 'legend' | 'caption';

export type Props = TextCommonProps & {
  /** HTML id attribute */
  id?: string;
  /** HTML element type to render as */
  as?: TextNodes;
  /** For label elements, associates label with form control */
  htmlFor?: string;
  /** ID for finding this element in unit tests */
  testID?: string;
  /** ARIA role attribute for accessibility https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Roles */
  role?: AriaRole;
  /** Temp support for onClick to support a case in StarRating, will be removed during the combo onPress */
  onClick?: React.MouseEventHandler<HTMLElement>;
};

const StyledText = styled(Box)<Omit<Props, 'as'>>(
  // Resets values in case of global styles
  {
    padding: '0',
    margin: '0',
    border: '0 none',
  },
  (props) => {
    const text: Record<string, { fontWeight: number; fontSize: string; lineHeight: number }> = props
      .theme?.text
      ? props.theme.text
      : roverTheme.text;
    // the theme has fontWeights, but historically we didn't have them in the hand rolled size variant styles
    // so now, to prevent a bunch of strange style issues, we remove the fontWeight styles here
    const textWithoutFontWeights = Object.fromEntries(
      // var is unused to remove it from the final object
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      Object.entries(text).map(([key, { fontWeight, ...value }]) => [key, value])
    );
    return variant({
      prop: 'size',
      variants: textWithoutFontWeights,
    })(props);
  },
  compose(textAlign, textColor, fontWeightSystem, textDecoration, textTransform, space), // Apply last to avoid being overwritten by resets and variant styles

  // support the "sx" prop.  This comes last so it will override any other styles
  (props) => css(props.sx)(props.theme)
);

function Text<C extends React.ElementType = 'span'>({
  as,
  size = '200',
  ...other
}: PolymorphicBoxPropsHelper<Props, C>): JSX.Element {
  const Component = as || 'span';
  return <StyledText {...other} webAs={Component} size={size} />;
}

/**
 * Text is the fundamental component for rendering inline text content.
 * Use for small amounts of text like labels, captions, and short phrases.
 *
 * READ THE DOCS: https://roverdotcom.atlassian.net/wiki/spaces/TECH/pages/5212766437/Kibble+Core+Text
 */
export default Text;
